import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  BookOpenCheck,
  Check,
  CheckCircle2,
  FileSearch,
  History,
  Plus,
  RotateCcw,
  Scale,
  Send,
  Sparkles,
  X,
} from "lucide-react";
import { createAiTask } from "../lib/api";
import {
  classNames,
  cleanTemplateName,
  compactText,
  formatDate,
} from "../lib/format";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  MetricStrip,
  PageHeader,
} from "./Shared";
import { annotateTemplatesByApplicability } from "../lib/regionFilter";

const initialRules = [
  {
    id: "subject-consistency",
    name: "签约主体一致性",
    category: "主体审查",
    description: "核对企业名称、员工或相对方身份、盖章主体和实际履行主体。",
  },
  {
    id: "required-clauses",
    name: "必备条款完整性",
    category: "文本审查",
    description: "按文书类型检查期限、金额、履行方式、解除条件和责任条款。",
  },
  {
    id: "signature-evidence",
    name: "签署与送达证据",
    category: "证据审查",
    description: "确认签署方式、送达地址、回执、附件和归档路径能够形成证据链。",
  },
  {
    id: "rights-balance",
    name: "权利义务对等性",
    category: "风险审查",
    description: "识别单方免责、过度违约责任、模糊授权和不合理限制。",
  },
];

function reviewReason(file) {
  // 根据模板元数据生成审校提示，帮助专家快速识别需重点关注的文书。
  if (file.applicabilityStatus?.layer === "hidden") return "疑似误标";
  if (file.previewError) return "解析异常";
  if (file.previewTruncated) return "长文抽检";
  return "条款校验";
}

function applicabilityLabel(file) {
  return file.applicabilityStatus?.reason || "待标注";
}

function applicabilityChipClass(file) {
  if (file.applicabilityStatus?.layer === "hidden") return "danger";
  if (["company", "district", "city", "province", "industry"].includes(file.applicabilityStatus?.layer)) return "success";
  return "warning";
}

function ExpertReview({
  catalog,
  moduleData,
  moduleStatus,
  selectedModuleKey,
  companyRegion,
  onModuleChange,
  onAskAi,
  onContextChange,
  decisions,
  setDecisions,
}) {
  const [selectedFileId, setSelectedFileId] = useState("");
  const [note, setNote] = useState("");
  const [submitStatus, setSubmitStatus] = useState("idle");

  const reviewQueue = useMemo(() => {
    if (!moduleData) return [];
    return annotateTemplatesByApplicability(moduleData.files, companyRegion, { includeHidden: true })
      .sort((first, second) => {
        const firstPriority = first.applicabilityStatus?.layer === "hidden" ? 0 : first.previewError ? 1 : first.previewTruncated ? 2 : 3;
        const secondPriority = second.applicabilityStatus?.layer === "hidden" ? 0 : second.previewError ? 1 : second.previewTruncated ? 2 : 3;
        return firstPriority - secondPriority;
      })
      .slice(0, 24);
  }, [companyRegion, moduleData]);

  useEffect(() => {
    if (!reviewQueue.length) {
      setSelectedFileId("");
      return;
    }
    if (!reviewQueue.some((file) => file.id === selectedFileId)) {
      setSelectedFileId(reviewQueue[0].id);
    }
  }, [reviewQueue, selectedFileId]);

  const selectedFile = reviewQueue.find((file) => file.id === selectedFileId);
  const completedCount = reviewQueue.filter((file) => decisions[file.id]).length;

  useEffect(() => {
    if (!selectedFile) return;
    onContextChange({
      page: "专家审校",
      currentProject: selectedFile.submoduleName || "",
      templateName: cleanTemplateName(selectedFile.fileName),
      templateId: selectedFile.id,
    });
  }, [onContextChange, selectedFile]);

  async function submitDecision(decision) {
    if (!selectedFile) return;
    setSubmitStatus("submitting");
    const reviewNote =
      note.trim() ||
      (decision === "approved"
        ? "专家确认当前模板可以进入后续调用。"
        : "专家要求补充或调整模板条款。");

    try {
      await createAiTask({
        taskKey: "expert-review-" + selectedFile.id + "-" + Date.now(),
        taskType: "OPTIMIZE_TEMPLATE",
        priority: decision === "changes" ? 9 : 6,
        payload: {
          moduleName: selectedFile.moduleName,
          moduleKey: selectedFile.moduleKey,
          scenarioName: selectedFile.submoduleName,
          templateName: cleanTemplateName(selectedFile.fileName),
          templateId: selectedFile.id,
          decision,
          expertNote: reviewNote,
          sourcePath: selectedFile.filePath,
        },
      });
      setDecisions((current) => ({
        ...current,
        [selectedFile.id]: {
          decision,
          note: reviewNote,
          reviewedAt: new Date().toISOString(),
        },
      }));
      setNote("");
      setSubmitStatus("saved");
    } catch (error) {
      setSubmitStatus("error");
    }
  }

  if (moduleStatus === "loading" || !moduleData) {
    return <LoadingState label="正在建立专家抽检队列" />;
  }

  return (
    <>
      <PageHeader
        eyebrow="专家端"
        title="文书审校工作台"
        description="以真实模板文件建立抽检队列，专家结论进入 AI 模板优化任务。"
        actions={
          onAskAi ? (
            <button
              className="secondary-button"
              type="button"
              onClick={() =>
                selectedFile &&
                onAskAi(
                  "请作为文书审校助手，检查“" +
                    cleanTemplateName(selectedFile.fileName) +
                    "”的主体、必备条款、履行风险和证据链。",
                )
              }
              disabled={!selectedFile}
            >
              <Sparkles size={16} aria-hidden="true" />
              AI 辅助审校
            </button>
          ) : null
        }
      />

      <MetricStrip
        items={[
          { label: "当前抽检", value: String(reviewQueue.length), note: moduleData.moduleName },
          { label: "已完成", value: String(completedCount), note: "本次会话" },
          {
            label: "疑似误标",
            value: String(reviewQueue.filter((file) => file.applicabilityStatus?.layer === "hidden").length),
            note: "范围不适用",
          },
          {
            label: "适用范围",
            value: String(reviewQueue.filter((file) => file.applicabilityStatus?.layer !== "hidden").length),
            note: companyRegion?.fullName || "通用审校",
          },
        ]}
      />

      <section className="expert-toolbar">
        <label className="select-field">
          <span>审校模块</span>
          <select value={selectedModuleKey} onChange={(event) => onModuleChange(event.target.value)}>
            {catalog.modules.map((module) => (
              <option key={module.moduleKey} value={module.moduleKey}>
                {module.moduleName}（{module.fileCount}）
              </option>
            ))}
          </select>
        </label>
        <div className="review-legend">
          <span><AlertTriangle size={14} /> 异常优先</span>
          <span><FileSearch size={14} /> 实际文件抽检</span>
        </div>
      </section>

      <div className="expert-review-layout">
        <section className="review-queue" aria-label="审校队列">
          <div className="panel-heading">
            <div>
              <h2>待审模板</h2>
              <span>{reviewQueue.length} 份</span>
            </div>
          </div>
          <div className="review-queue-list">
            {reviewQueue.map((file) => {
              const decision = decisions[file.id]?.decision;
              return (
                <button
                  className={classNames(
                    selectedFileId === file.id && "active",
                    decision && "reviewed",
                  )}
                  key={file.id}
                  type="button"
                  onClick={() => {
                    setSelectedFileId(file.id);
                    setSubmitStatus("idle");
                    setNote(decisions[file.id]?.note || "");
                  }}
                >
                  <span className="queue-file-icon">
                    {decision ? <CheckCircle2 size={17} /> : <FileSearch size={17} />}
                  </span>
                  <span>
                    <strong>{cleanTemplateName(file.fileName)}</strong>
                    <small>{file.submoduleName} / {reviewReason(file)} / {applicabilityLabel(file)}</small>
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        <section className="review-inspector">
          {selectedFile ? (
            <>
              <header className="review-document-header">
                <div>
                  <span>{selectedFile.moduleName} / {selectedFile.submoduleName}</span>
                  <h2>{cleanTemplateName(selectedFile.fileName)}</h2>
                </div>
                <span className={classNames("status-chip", applicabilityChipClass(selectedFile))}>
                  {applicabilityLabel(selectedFile)}
                </span>
              </header>

              <div className="review-document">
                <pre>{selectedFile.previewText || selectedFile.previewError || "当前文件暂无文本预览。"}</pre>
              </div>

              <div className="review-form">
                <label className="field-block">
                  <span>专家意见</span>
                  <textarea
                    rows="4"
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    placeholder="记录适用范围、条款问题、修改建议或通过依据"
                  />
                </label>
                <div className="review-checks">
                  {initialRules.map((rule) => (
                    <span key={rule.id}>
                      <Check size={14} aria-hidden="true" />
                      {rule.name}
                    </span>
                  ))}
                </div>
                {submitStatus === "saved" ? (
                  <p className="inline-success">审校结论已保存，并进入 AI 优化任务。</p>
                ) : null}
                {submitStatus === "error" ? (
                  <p className="inline-error">审校任务提交失败，请确认原系统后端已连接。</p>
                ) : null}
                <div className="form-actions">
                  <button
                    className="secondary-button danger-button"
                    type="button"
                    onClick={() => submitDecision("changes")}
                    disabled={submitStatus === "submitting"}
                  >
                    <RotateCcw size={16} aria-hidden="true" />
                    需修改
                  </button>
                  <button
                    className="primary-button"
                    type="button"
                    onClick={() => submitDecision("approved")}
                    disabled={submitStatus === "submitting"}
                  >
                    <Send size={16} aria-hidden="true" />
                    通过并沉淀
                  </button>
                </div>
              </div>
            </>
          ) : (
            <EmptyState title="当前模块没有可审模板" />
          )}
        </section>
      </div>
    </>
  );
}

function ExpertRules({ rules, setRules, onContextChange }) {
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ name: "", category: "", description: "" });

  useEffect(() => {
    onContextChange({ page: "专家规则知识库", currentProject: "", templateName: "", templateId: "" });
  }, [onContextChange]);

  function addRule(event) {
    event.preventDefault();
    if (!draft.name.trim() || !draft.description.trim()) return;
    setRules((current) => [
      ...current,
      { ...draft, id: "custom-" + Date.now(), category: draft.category || "自定义规则" },
    ]);
    setDraft({ name: "", category: "", description: "" });
    setCreating(false);
  }

  return (
    <>
      <PageHeader
        eyebrow="专家端"
        title="规则知识库"
        description="专家维护可解释、可追溯的审校规则，供 AI 在模板生成和审查时引用。"
        actions={
          <button className="primary-button" type="button" onClick={() => setCreating(true)}>
            <Plus size={16} aria-hidden="true" />
            新建规则
          </button>
        }
      />

      {creating ? (
        <form className="inline-create-form" onSubmit={addRule}>
          <div className="inline-create-header">
            <strong>新增审校规则</strong>
            <button className="icon-button" type="button" onClick={() => setCreating(false)} aria-label="取消新建">
              <X size={17} />
            </button>
          </div>
          <div className="form-grid">
            <label className="field-block">
              <span>规则名称</span>
              <input
                value={draft.name}
                onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
                placeholder="例如：试用期期限校验"
                required
              />
            </label>
            <label className="field-block">
              <span>规则分类</span>
              <input
                value={draft.category}
                onChange={(event) => setDraft((current) => ({ ...current, category: event.target.value }))}
                placeholder="例如：劳动合同"
              />
            </label>
            <label className="field-block full-field">
              <span>判断依据</span>
              <textarea
                rows="3"
                value={draft.description}
                onChange={(event) => setDraft((current) => ({ ...current, description: event.target.value }))}
                placeholder="写明适用场景、判断条件和建议动作"
                required
              />
            </label>
          </div>
          <div className="form-actions">
            <button className="primary-button" type="submit">保存规则</button>
          </div>
        </form>
      ) : null}

      <section className="rules-list">
        {rules.map((rule) => (
          <article key={rule.id}>
            <div className="rule-index"><Scale size={17} aria-hidden="true" /></div>
            <div>
              <span>{rule.category}</span>
              <h2>{rule.name}</h2>
              <p>{rule.description}</p>
            </div>
            <span className="status-chip success">启用</span>
          </article>
        ))}
      </section>
    </>
  );
}

function ExpertCases({ decisions, onContextChange }) {
  const cases = Object.entries(decisions);

  useEffect(() => {
    onContextChange({ page: "专家案例沉淀", currentProject: "", templateName: "", templateId: "" });
  }, [onContextChange]);

  return (
    <>
      <PageHeader
        eyebrow="专家端"
        title="案例沉淀"
        description="已完成的审校结论形成模板实践样本，供后续同类文书复用。"
      />
      {cases.length ? (
        <section className="case-list">
          {cases.map(([id, item]) => (
            <article key={id}>
              <History size={18} aria-hidden="true" />
              <div>
                <strong>{item.decision === "approved" ? "通过案例" : "修改案例"}</strong>
                <p>{item.note}</p>
              </div>
              <time>{formatDate(item.reviewedAt)}</time>
            </article>
          ))}
        </section>
      ) : (
        <EmptyState
          title="还没有已沉淀案例"
          description="完成一次模板审校后，结论会在这里形成可复用样本。"
        />
      )}
    </>
  );
}

export default function ExpertWorkspace({
  view,
  catalog,
  catalogStatus,
  catalogError,
  onCatalogRetry,
  moduleData,
  moduleStatus,
  selectedModuleKey,
  companyRegion,
  onModuleChange,
  onAskAi,
  onContextChange,
}) {
  const [rules, setRules] = useState(initialRules);
  const [decisions, setDecisions] = useState({});

  if (catalogStatus === "loading") return <LoadingState label="正在准备专家工作台" />;
  if (catalogStatus === "error" || !catalog) {
    return <ErrorState message={catalogError} onRetry={onCatalogRetry} />;
  }

  if (view === "rules") {
    return <ExpertRules rules={rules} setRules={setRules} onContextChange={onContextChange} />;
  }
  if (view === "cases") {
    return <ExpertCases decisions={decisions} onContextChange={onContextChange} />;
  }
  return (
    <ExpertReview
      catalog={catalog}
      moduleData={moduleData}
      moduleStatus={moduleStatus}
      selectedModuleKey={selectedModuleKey}
      companyRegion={companyRegion}
      onModuleChange={onModuleChange}
      onAskAi={onAskAi}
      onContextChange={onContextChange}
      decisions={decisions}
      setDecisions={setDecisions}
    />
  );
}
